/**
 * Drags the edge of an editor's side panel (the grey bar between the settings and the preview).
 *
 * What sits beside the panel is mostly an iframe: the widget preview, or the editor itself. Once
 * the cursor crosses into an iframe, that document receives the mouse events, so with window
 * listeners the resize stopped at the iframe's edge and the release was lost there, leaving the
 * panel following the cursor afterwards. Capturing the pointer on the handle keeps every move
 * and the release coming to it wherever the cursor goes. The `panel-resizing` body class also
 * makes iframes ignore the pointer while dragging (see `_nav-shell.scss`), for nested editor
 * frames and any browser that routes captured events into a frame anyway.
 *
 * Bind it to the handle's `pointerdown`; `onResize` receives each new width, never below
 * `minWidth`.
 */
export function startPanelResize(
  event: PointerEvent, startWidth: number, onResize: (width: number) => void, minWidth = 200
): void {
  if (event.button !== 0) {
    return;
  }
  event.preventDefault();

  const handle = event.currentTarget as HTMLElement;
  const body = document.body;
  const startX = event.clientX;
  let ended = false;

  body.classList.add('panel-resizing');
  body.style.userSelect = 'none';
  body.style.cursor = 'ew-resize';
  try {
    handle.setPointerCapture(event.pointerId);
  } catch {
    // The pointer is already gone (released before this ran); the iframe rule still covers the drag.
  }

  // Captured events are dispatched to the handle and bubble up, so window listeners see them
  // with or without the capture.
  const onMove = (move: PointerEvent) => {
    if (move.pointerId === event.pointerId) {
      onResize(Math.max(minWidth, startWidth + move.clientX - startX));
    }
  };
  const onEnd = (end: PointerEvent) => {
    if (ended || end.pointerId !== event.pointerId) {
      return;
    }
    ended = true;
    body.classList.remove('panel-resizing');
    body.style.userSelect = '';
    body.style.cursor = '';
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onEnd);
    window.removeEventListener('pointercancel', onEnd);
    window.removeEventListener('lostpointercapture', onEnd);
  };

  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onEnd);
  window.addEventListener('pointercancel', onEnd);
  window.addEventListener('lostpointercapture', onEnd);
}
